/**
* カート情報管理
*/
import cf from '@/mixins/commonFunctions';
import { PLAN_MAP, PLAN_TIME_LIMITS } from '@/data/plan/plans.js';

/**
 * カート内容から有効なプランを判定し、ローカルストレージに保存する
 * @param {object} state - Vuexのstateオブジェクト
 */
const updateActivePlans = (state) => {
  const planNames = Object.keys(PLAN_MAP);
  const activePlans = state.items
    .filter((item) => planNames.includes(item.name))
    .map((item) => item.name);

  const existingPlans = JSON.parse(localStorage.getItem('activePlans')) || [];

  // 新しいプランを既存のプランに追加し、重複を排除
  const updatedPlans = Array.from(new Set([...existingPlans, ...activePlans]));

  if (updatedPlans.length > 0) {
    localStorage.setItem('activePlans', JSON.stringify(updatedPlans));
  } else {
    localStorage.removeItem('activePlans');
  }
};


/**
 * 有効なプランに基づき、対象商品の価格をstateに直接上書きする
 * @param {object} state - Vuexのstateオブジェクト
 */
const updateItemPrices = (state) => {
  const activePlans = JSON.parse(localStorage.getItem('activePlans')) || [];

  // 割引対象となるカテゴリをすべて集める
  const targetCategories = new Set();
  activePlans.forEach((planName) => {
    if (PLAN_MAP[planName]) {
      PLAN_MAP[planName].forEach((category) => {
        targetCategories.add(category);
      });
    }
  });

  state.items.forEach((item) => {
    const isTargetItem = item.menus && targetCategories.has(item.menus.name);
    if (isTargetItem) {
      item.price = 0;
    } else {
      item.price = item.original_price;
    }
  });
};

export default {
  namespaced: true,
  state: {
    items: [],
    drinkPlanEndTime: JSON.parse(localStorage.getItem('drinkPlanEndTime')) || null,
    activeDrinkPlan: localStorage.getItem('activeDrinkPlan') || null,
  },
  actions: {
    // ★ お会計（セッション終了）時に呼び出すアクション
    endSession({ dispatch, commit }) {
      localStorage.removeItem('activePlans');
      localStorage.removeItem('drinkPlanEndTime');
      localStorage.removeItem('activeDrinkPlan');
      dispatch('orderhistory/clearHistory', null, { root: true });
      dispatch('reset');
      commit('clearDrinkPlan');
    },

    endDrinkPlan({ commit }) {
      localStorage.removeItem('drinkPlanEndTime');
      localStorage.removeItem('activeDrinkPlan');
      commit('clearDrinkPlan');
    },

    startDrinkPlanTimer({ state, commit }) {
      const planNames = Object.keys(PLAN_TIME_LIMITS);
      const drinkPlanItem = state.items.find((item) => planNames.includes(item.name));

      if (drinkPlanItem) {
        const timeLimit = PLAN_TIME_LIMITS[drinkPlanItem.name];
        const now = new Date();
        const endTime = now.getTime() + timeLimit * 60 * 1000;

        localStorage.setItem('drinkPlanEndTime', endTime);
        localStorage.setItem('activeDrinkPlan', drinkPlanItem.name);
        
        commit('setDrinkPlan', { endTime, planName: drinkPlanItem.name });
      }
    },

    /**
     * カートに商品を追加
     */
    addCart({ commit, dispatch }, item) {
      if (!item?.id) return;
      const ls = cf.getLocalStorage('sma_order');
      const saveData = ls || {};
      if (!ls || !ls.cart || !ls.cart.items.length) {
        saveData.cart = { items: [item] };
      } else if (!ls.cart.items.find((i) => i.id === item.id)) {
        saveData.cart.items.push(item);
      }
      cf.saveLocalStorage(saveData, 'sma_order');
      commit('setCart');
      dispatch('orderhistory/recalculatePrices', null, { root: true });
    },
    /**
     * カートから商品を削除
     */
    removeCart({ commit, dispatch }, itemId) {
      const ls = cf.getLocalStorage('sma_order');
      const saveData = ls || {};
      let newArr = [];
      if (ls?.cart?.items.length) {
        newArr = ls.cart.items.filter((item) => item.id !== itemId);
      }
      saveData.cart = { items: newArr };
      cf.saveLocalStorage(saveData, 'sma_order');
      commit('setCart');
      dispatch('orderhistory/recalculatePrices', null, { root: true });
    },

    /**
     * カートの商品数を+1する
     */
    addQuantity({ commit, dispatch }, itemId) {
      const ls = cf.getLocalStorage('sma_order');
      const saveData = ls || {};
      const targetCartItemIndex = saveData.cart?.items.findIndex((item) => item.id === itemId);
      if (targetCartItemIndex !== -1 && saveData.cart?.items) {
        saveData.cart.items[targetCartItemIndex].quantity += 1;
        cf.saveLocalStorage(saveData, 'sma_order');
      }
      commit('setCart');
      dispatch('orderhistory/recalculatePrices', null, { root: true });
    },
    /**
     * カートの商品数を-1する
     */
    subtractQuantity({ commit, dispatch }, itemId) {
      const ls = cf.getLocalStorage('sma_order');
      const saveData = ls || {};
      const targetCartItemIndex = saveData.cart?.items.findIndex((item) => item.id === itemId);
      if (targetCartItemIndex !== -1 && saveData.cart?.items) {
        saveData.cart.items[targetCartItemIndex].quantity -= 1;
        cf.saveLocalStorage(saveData, 'sma_order');
      }
      commit('setCart');
      dispatch('orderhistory/recalculatePrices', null, { root: true });
    },

    /**
     * カートをリセットする
     */
    reset({ commit }) {
      const saveData = cf.getLocalStorage('sma_order') || {};
      saveData.cart = { items: [] };
      cf.saveLocalStorage(saveData, 'sma_order');
      commit('reset');
    },
    async transferCartToOrderHistory({ state, dispatch }) {
      // 1. カートが空の場合は何もしない
      if (state.items.length === 0) {
        return;
      }

      try {
        // 2. 'orderHistory'ストアのアクションを呼び出し、カートの中身を渡す
        //    { root: true } を付けて他のモジュールのアクションを呼び出す
        await dispatch('orderhistory/addItemsFromCart', [...state.items], { root: true });

        // 3. 移動が成功したら、カートをリセットするアクションを呼び出す
        await dispatch('reset');

        console.log('カートの中身を履歴に移動しました。');
      } catch (error) {
        console.error('カートの移動中にエラーが発生しました:', error);
      }
    },
  },
  mutations: {
    /**
     * カートをstoreにセットし、価格を更新する
     */
    setCart(state) {
      const ls = cf.getLocalStorage('sma_order');
      const items = [];
      const lsItems = ls?.cart?.items || [];
      lsItems.forEach((item) => {
        items.push({
          ...item,
          original_price: item.original_price || item.price,
        });
      });
      state.items = items;

      updateActivePlans(state);
      updateItemPrices(state);
    },

    /**
     * カートをリセットする
     */
    reset(state) {
      state.items = [];
    },

    /**
     * 飲み放題プランの情報をセットする
     */
    setDrinkPlan(state, { endTime, planName }) {
      state.drinkPlanEndTime = endTime;
      state.activeDrinkPlan = planName;
    },

    /**
     * 飲み放題プランの情報をクリアする
     */
    clearDrinkPlan(state) {
      state.drinkPlanEndTime = null;
      state.activeDrinkPlan = null;
    },
  },
};
